// Framework for optional 2× earned rewards via rewarded ads.
// Register new kinds in EARNED_REWARD_KINDS + DOUBLE_APPLIERS as rewards are defined.

import { grantTreats, formatTreats } from "./treats";
import { HEART_REWARD_STUB_DELAY_MS } from "./heartMonetization";

const LS_REWARD_DOUBLE_AD_DAY_KEY = "mmu_reward_double_ad_day_v1";
const LS_REWARD_DOUBLE_AD_COUNT_KEY = "mmu_reward_double_ad_count_v1";

/** Daily cap for 2× earned-reward ads (stub until real SDK). */
export const REWARD_DOUBLE_AD_DAILY_CAP = 10;

export const EARNED_REWARD_KINDS = {
    TREATS: "treats",
};

const DOUBLE_APPLIERS = {
    [EARNED_REWARD_KINDS.TREATS]: (offer, state = {}) => {
        const bonus = Math.max(0, Math.floor(Number(offer?.amount) || 0));
        if (bonus <= 0) {
            return null;
        }

        return {
            treats: grantTreats(state.treats, bonus),
            runTreatsEarned:
                Math.max(0, Math.floor(Number(state.runTreatsEarned) || 0)) +
                bonus,
        };
    },
};

function todayKey(now = Date.now()) {
    return new Date(now).toISOString().slice(0, 10);
}

function readRewardDoubleAdUsage(now = Date.now()) {
    try {
        const day = localStorage.getItem(LS_REWARD_DOUBLE_AD_DAY_KEY);
        const count = Number(localStorage.getItem(LS_REWARD_DOUBLE_AD_COUNT_KEY));
        if (day !== todayKey(now) || !Number.isFinite(count)) {
            return { day: todayKey(now), count: 0 };
        }
        return { day, count: Math.max(0, Math.floor(count)) };
    } catch {
        return { day: todayKey(now), count: 0 };
    }
}

function writeRewardDoubleAdUsage(count, now = Date.now()) {
    try {
        localStorage.setItem(LS_REWARD_DOUBLE_AD_DAY_KEY, todayKey(now));
        localStorage.setItem(
            LS_REWARD_DOUBLE_AD_COUNT_KEY,
            String(Math.max(0, Math.floor(count)))
        );
    } catch {
        // ignore localStorage failures
    }
}

export function createEarnedRewardOffer({
    id,
    kind,
    label,
    amount = 0,
    meta = null,
}) {
    if (!id || !kind || !label) {
        return null;
    }

    return {
        id: String(id),
        kind: String(kind),
        label: String(label),
        amount: Math.max(0, Math.floor(Number(amount) || 0)),
        meta: meta && typeof meta === "object" ? { ...meta } : null,
    };
}

/**
 * Build doublable reward line items for a recap win.
 * Pass `extras` to append custom offers from future reward sources.
 */
export function buildEarnedRewardOffers({
    outcome = "loss",
    treatsEarned = 0,
    extras = [],
} = {}) {
    if (outcome !== "win") {
        return [];
    }

    const offers = [];
    const treats = Math.max(0, Math.floor(Number(treatsEarned) || 0));

    if (treats > 0) {
        const offer = createEarnedRewardOffer({
            id: "treats",
            kind: EARNED_REWARD_KINDS.TREATS,
            label: formatTreats(treats),
            amount: treats,
        });
        if (offer) {
            offers.push(offer);
        }
    }

    const extraOffers = Array.isArray(extras)
        ? extras.filter(Boolean)
        : [];

    return [...offers, ...extraOffers];
}

export function isEarnedRewardKindSupported(kind) {
    return Boolean(kind && DOUBLE_APPLIERS[kind]);
}

export function getRewardedDoubleAdStatus(now = Date.now(), options = {}) {
    if (options.adsRemoved) {
        return {
            available: false,
            remainingToday: 0,
            dailyCap: REWARD_DOUBLE_AD_DAILY_CAP,
            usedToday: 0,
            isPreview: true,
            adsRemoved: true,
        };
    }

    const usage = readRewardDoubleAdUsage(now);
    const remaining = Math.max(0, REWARD_DOUBLE_AD_DAILY_CAP - usage.count);

    return {
        available: remaining > 0,
        remainingToday: remaining,
        dailyCap: REWARD_DOUBLE_AD_DAILY_CAP,
        usedToday: usage.count,
        isPreview: true,
        adsRemoved: false,
    };
}

function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function watchRewardedAdForDouble(now = Date.now(), options = {}) {
    const status = getRewardedDoubleAdStatus(now, options);

    if (status.adsRemoved) {
        const error = new Error("Ads are removed on this account.");
        error.code = "ads-removed";
        throw error;
    }

    if (!status.available) {
        const error = new Error("Daily rewarded-ad limit reached.");
        error.code = "ad-cap";
        throw error;
    }

    if (!options.skipDelay) {
        await delay(HEART_REWARD_STUB_DELAY_MS);
    }

    writeRewardDoubleAdUsage(status.usedToday + 1, now);

    return {
        granted: true,
        isPreview: true,
        remainingToday: Math.max(0, status.remainingToday - 1),
    };
}

export function canOfferRewardDouble(
    offer,
    { doubledOfferIds = [], adsRemoved = false, adStatus = null } = {}
) {
    if (!offer || adsRemoved) {
        return false;
    }

    const doubled = Array.isArray(doubledOfferIds) ? doubledOfferIds : [];
    if (doubled.includes(offer.id)) {
        return false;
    }

    if (!isEarnedRewardKindSupported(offer.kind)) {
        return false;
    }

    if (adStatus && adStatus.available === false) {
        return false;
    }

    return Math.max(0, Math.floor(Number(offer.amount) || 0)) > 0;
}

export function getVisibleEarnedRewardOffers(
    offers = [],
    { doubledOfferIds = [], adsRemoved = false, adStatus = null } = {}
) {
    return (Array.isArray(offers) ? offers : []).filter((offer) =>
        canOfferRewardDouble(offer, { doubledOfferIds, adsRemoved, adStatus })
    );
}

export function getRewardDoubleAdLabel(offer) {
    if (!offer?.label) {
        return "2× Reward";
    }
    return `2× ${offer.label}`;
}

export function getRewardDoubleButtonLabel(offer) {
    return `Watch Ad · ${getRewardDoubleAdLabel(offer)}`;
}

export function applyEarnedRewardDouble(offer, state = {}) {
    const applier = offer?.kind ? DOUBLE_APPLIERS[offer.kind] : null;
    if (!applier) {
        return null;
    }
    return applier(offer, state);
}
